import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Row, Col, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { notify } from '../../stores/useNotificationStore';
import SearchSelectField, { SearchOption } from '../../components/SearchSelectField';

interface IFormData {
  tipo: string;
  nome: string;
  email: string;
  telefone: string;
  funcao: string;          // Cargo
  departamento: string;    // nome (Cadastro Auxiliar)
  diretoria: string;       // nome (Cadastro Auxiliar)
  gestor_id: string;       // id de outra Pessoa
  empresa_terceiro: string; // nome do terceiro (Contratos)
}

const initialState: IFormData = {
  tipo: '',
  nome: '',
  email: '',
  telefone: '',
  funcao: '',
  departamento: '',
  diretoria: '',
  gestor_id: '',
  empresa_terceiro: '',
};

const isEmpregado = (tipo: string) => tipo.trim().toLowerCase() === 'empregado';

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
};

const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const PersonRegistration: React.FC = () => {
  const [formData, setFormData] = useState<IFormData>(initialState);
  const [tipoOptions, setTipoOptions] = useState<SearchOption[]>([]);
  const [deptOptions, setDeptOptions] = useState<SearchOption[]>([]);
  const [diretoriaOptions, setDiretoriaOptions] = useState<SearchOption[]>([]);
  const [gestorOptions, setGestorOptions] = useState<SearchOption[]>([]);
  const [fornecedorOptions, setFornecedorOptions] = useState<SearchOption[]>([]);
  const [pessoasRaw, setPessoasRaw] = useState<{ id: number; nome: string; email: string }[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  // Aviso pós-cadastro rápido de um Gestor (via +): modal com OK, para não passar despercebido.
  const [gestorCriadoModal, setGestorCriadoModal] = useState<string | null>(null);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { empresaId } = useInterfaceStore();

  // ── Carrega os cadastros de origem (auxiliares, pessoas, contratos) ──
  useEffect(() => {
    const load = async () => {
      setIsLoadingData(true);
      try {
        const [tipoRes, deptRes, dirRes, pessoasRes, contratosRes] = await Promise.all([
          api.getCadastrosAuxiliares('tipo_pessoa', empresaId ?? undefined),
          api.getCadastrosAuxiliares('departamento', empresaId ?? undefined),
          api.getCadastrosAuxiliares('diretoria', empresaId ?? undefined),
          api.getPessoas(empresaId ?? undefined),
          api.getContratos(),
        ]);
        const auxToOpt = (rows: any[]): SearchOption[] =>
          (rows || []).filter(r => r.ativo !== false).map(r => ({ key: r.nome, label: r.nome }));
        if (tipoRes.success) setTipoOptions(auxToOpt(tipoRes.data as any[]));
        if (deptRes.success) setDeptOptions(auxToOpt(deptRes.data as any[]));
        if (dirRes.success) setDiretoriaOptions(auxToOpt(dirRes.data as any[]));
        if (pessoasRes.success) {
          const outrasPessoas = (pessoasRes.data as any[]).filter(p => !id || String(p.id) !== id);
          setGestorOptions(outrasPessoas.map(p => ({ key: String(p.id), label: p.nome })));
          setPessoasRaw(outrasPessoas.map(p => ({ id: p.id, nome: p.nome, email: p.email || '' })));
        }
        if (contratosRes.success) {
          const seen = new Set<string>();
          const opts: SearchOption[] = [];
          for (const c of (contratosRes.data as any[])) {
            const nome = c.razao_social || c.nome_fantasia || c.contratante_nome;
            if (nome && !seen.has(nome)) {
              seen.add(nome);
              opts.push({ key: nome, label: nome, extra: c.cnpj || c.contratante_cnpj || undefined });
            }
          }
          setFornecedorOptions(opts);
        }
      } catch (err) {
        console.error('Erro ao carregar dados de apoio:', err);
      } finally {
        setIsLoadingData(false);
      }
    };
    load();
  }, [empresaId, id]);

  // ── Carrega a pessoa em edição ──
  useEffect(() => {
    if (!id) return;
    const loadPessoa = async () => {
      setIsLoading(true);
      try {
        const response = await api.getPessoa(parseInt(id));
        if (response.success && response.data) {
          const p = response.data as any;
          setFormData({
            tipo: p.tipo || '',
            nome: p.nome || '',
            email: p.email || '',
            telefone: p.telefone || '',
            funcao: p.funcao || '',
            departamento: p.departamento || '',
            diretoria: p.diretoria || '',
            gestor_id: p.gestor_id ? String(p.gestor_id) : '',
            empresa_terceiro: p.empresa_terceiro || '',
          });
        } else {
          setError('Pessoa não encontrada.');
          navigate('/app/pessoas/list');
        }
      } catch {
        setError('Erro ao carregar dados.');
      } finally {
        setIsLoading(false);
      }
    };
    loadPessoa();
  }, [id, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'telefone') {
      setFormData(prev => ({ ...prev, telefone: formatPhone(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Ao escolher o Tipo, se for Empregado limpa o campo Fornecedor (não se aplica).
  const handleTipoSelect = (opt: SearchOption | null) => {
    const novoTipo = opt?.key || '';
    setFormData(prev => ({
      ...prev,
      tipo: novoTipo,
      empresa_terceiro: isEmpregado(novoTipo) ? '' : prev.empresa_terceiro,
    }));
  };

  // Cria um novo item de Cadastro Auxiliar direto do formulário (botão "+").
  const criarAuxiliar = useCallback(async (
    tipo: string,
    nome: string,
    setOptions: React.Dispatch<React.SetStateAction<SearchOption[]>>,
  ): Promise<SearchOption | null> => {
    if (!empresaId) { notify.warning('Selecione uma empresa antes de cadastrar.'); return null; }
    try {
      const res = await api.createCadastroAuxiliar({ tipo, nome, empresa_id: empresaId });
      if (res.success) {
        const opt: SearchOption = { key: nome, label: nome };
        setOptions(prev => [...prev, opt]);
        notify.success('Cadastrado com sucesso!');
        return opt;
      }
      notify.error((res.error as string) || 'Erro ao cadastrar.');
      return null;
    } catch {
      notify.error('Erro de conexão ao cadastrar.');
      return null;
    }
  }, [empresaId]);

  // Cadastra rapidamente uma Pessoa (gestor) com o nome digitado (botão "+").
  // A pessoa é criada como Empregado da empresa atual e pode ser completada depois.
  const criarGestor = useCallback(async (nome: string, email?: string): Promise<SearchOption | null> => {
    if (!empresaId) { notify.warning('Selecione uma empresa antes de cadastrar.'); return null; }
    const emailTrim = (email || '').trim();
    if (!emailTrim) { notify.warning('Informe o e-mail do novo Gestor.'); return null; }
    if (!isValidEmail(emailTrim)) { notify.warning('Informe um e-mail válido.'); return null; }
    // Evita nomes duplicados: se já existe uma Pessoa com este e-mail, não cadastra de novo.
    const existente = pessoasRaw.find(p => p.email.trim().toLowerCase() === emailTrim.toLowerCase());
    if (existente) {
      notify.error(`Já existe uma pessoa cadastrada com este e-mail: ${existente.nome}.`);
      return null;
    }
    try {
      const res = await api.createPessoa({ empresa_id: empresaId, tipo: 'Empregado', nome, email: emailTrim });
      if (res.success && res.data) {
        const novoId = (res.data as any).id;
        const opt: SearchOption = { key: String(novoId), label: nome };
        setGestorOptions(prev => [...prev, opt]);
        setPessoasRaw(prev => [...prev, { id: novoId, nome, email: emailTrim }]);
        setGestorCriadoModal(nome);
        return opt;
      }
      notify.error((res.error as string) || 'Erro ao cadastrar pessoa.');
      return null;
    } catch {
      notify.error('Erro de conexão ao cadastrar pessoa.');
      return null;
    }
  }, [empresaId, pessoasRaw]);

  const validate = (): boolean => {
    if (!formData.tipo.trim()) { setError('Tipo é obrigatório.'); return false; }
    if (!formData.nome.trim()) { setError('Nome é obrigatório.'); return false; }
    if (!empresaId) { setError('Nenhuma empresa selecionada.'); return false; }
    if (formData.email && !isValidEmail(formData.email)) {
      setError('E-mail inválido.'); return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    if (!validate()) return;

    setIsLoading(true);
    try {
      const payload = {
        empresa_id: empresaId,
        tipo: formData.tipo,
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        funcao: formData.funcao,
        departamento: formData.departamento,
        diretoria: formData.diretoria,
        gestor_id: formData.gestor_id || null,
        empresa_terceiro: isEmpregado(formData.tipo) ? '' : formData.empresa_terceiro,
      };

      const response = id
        ? await api.updatePessoa(parseInt(id), payload)
        : await api.createPessoa(payload);

      if (response.success) {
        notify.success(id ? 'Pessoa atualizada com sucesso!' : 'Pessoa cadastrada com sucesso!');
        navigate('/app/pessoas/list');
      } else {
        setError((response.error as string) || 'Erro ao salvar.');
      }
    } catch {
      setError('Erro de conexão ao tentar salvar.');
    } finally {
      setIsLoading(false);
    }
  };

  const empregado = isEmpregado(formData.tipo);
  const emailInvalid = !!formData.email && !isValidEmail(formData.email);

  if (isLoading && id) {
    return (
      <Container fluid className="p-4 text-center">
        <Spinner animation="border" /> <span>Carregando...</span>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <h1 className="mb-4">{id ? 'Editar Pessoa' : 'Cadastro de Pessoa'}</h1>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Form>
        <Row className="g-3">
          {/* Tipo (Cadastro Auxiliar: tipo_pessoa) */}
          <Col md={6}>
            <Form.Group>
              <Form.Label>Tipo *</Form.Label>
              <SearchSelectField
                placeholder="Pesquisar no cadastro Auxiliar: Tipo de Pessoa"
                options={tipoOptions}
                value={formData.tipo}
                allowFreeText
                onSelect={handleTipoSelect}
                onAdd={nome => criarAuxiliar('tipo_pessoa', nome, setTipoOptions)}
                isInvalid={!!error && !formData.tipo.trim()}
              />
            </Form.Group>
          </Col>

          {/* Fornecedor / Empresa Terceiro (Contratos) — só habilitado se Tipo ≠ Empregado */}
          <Col md={6}>
            <Form.Group>
              <Form.Label>Fornecedor {!empregado && <span className="text-danger">*</span>}</Form.Label>
              <SearchSelectField
                placeholder="Pesquisar no cadastro de Fornecedores"
                options={fornecedorOptions}
                value={formData.empresa_terceiro}
                allowFreeText
                disabled={empregado}
                onSelect={opt => setFormData(prev => ({ ...prev, empresa_terceiro: opt?.key || '' }))}
              />
              <Form.Text className="text-muted">
                {empregado
                  ? 'Este campo só é habilitado quando o Tipo da Pessoa NÃO for Empregado.'
                  : 'Vincula o cadastro e contratos de terceiros.'}
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="nome">
              <Form.Label>Nome *</Form.Label>
              <Form.Control type="text" name="nome" value={formData.nome} onChange={handleChange} />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="email">
              <Form.Label>E-mail</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={emailInvalid}
              />
              <Form.Control.Feedback type="invalid">E-mail inválido.</Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="telefone">
              <Form.Label>Telefone</Form.Label>
              <Form.Control
                type="tel"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="funcao">
              <Form.Label>Cargo</Form.Label>
              <Form.Control type="text" name="funcao" value={formData.funcao} onChange={handleChange} />
            </Form.Group>
          </Col>

          {/* Área ou Departamento (Cadastro Auxiliar: departamento) */}
          <Col md={4}>
            <Form.Group>
              <Form.Label>Departamento</Form.Label>
              <SearchSelectField
                placeholder="Pesquisar no cadastro de Departamentos"
                options={deptOptions}
                value={formData.departamento}
                allowFreeText
                onSelect={opt => setFormData(prev => ({ ...prev, departamento: opt?.key || '' }))}
                onAdd={nome => criarAuxiliar('departamento', nome, setDeptOptions)}
              />
            </Form.Group>
          </Col>

          {/* Gestor Imediato (outra Pessoa) */}
          <Col md={4}>
            <Form.Group>
              <Form.Label>Gestor Imediato</Form.Label>
              <SearchSelectField
                placeholder="Pesquisar no cadastro de Pessoas"
                options={gestorOptions}
                value={formData.gestor_id}
                disabled={isLoadingData}
                onSelect={opt => setFormData(prev => ({ ...prev, gestor_id: opt?.key || '' }))}
                onAdd={criarGestor}
                extraField={{ label: 'E-mail', type: 'email', required: true }}
              />
            </Form.Group>
          </Col>

          {/* Diretoria (Cadastro Auxiliar: diretoria) */}
          <Col md={4}>
            <Form.Group>
              <Form.Label>Diretoria</Form.Label>
              <SearchSelectField
                placeholder="Pesquisar no cadastro de diretoria"
                options={diretoriaOptions}
                value={formData.diretoria}
                allowFreeText
                onSelect={opt => setFormData(prev => ({ ...prev, diretoria: opt?.key || '' }))}
                onAdd={nome => criarAuxiliar('diretoria', nome, setDiretoriaOptions)}
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="text-end mt-4">
          <Button variant="secondary" className="me-2" onClick={() => navigate('/app/pessoas/list')}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar'}
          </Button>
        </div>
      </Form>

      {/* Aviso pós-cadastro rápido de Gestor: modal com OK, para o usuário não deixar passar
          despercebido que o registro ficou incompleto (só Nome e E-mail). */}
      <Modal show={!!gestorCriadoModal} onHide={() => setGestorCriadoModal(null)} centered>
        <Modal.Header closeButton><Modal.Title className="fs-6">Pessoa cadastrada</Modal.Title></Modal.Header>
        <Modal.Body>
          <strong>{gestorCriadoModal}</strong> foi cadastrado(a) com Nome e E-mail. Complete os
          demais dados no cadastro de Pessoas, se necessário.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setGestorCriadoModal(null)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PersonRegistration;
